/**
 * DetailModal Component
 * Full-screen modal with term details, examples, code, and minicourses
 */

import React, { useEffect, useRef } from "react";
import type { Language, LexiconItem } from "../types";
import { LEVEL_CONFIG, PILLAR_CONFIG, COURSE_TYPE_CONFIG } from "../types";
import MiniCourseCard from "./MiniCourseCard";

interface DetailModalProps {
  item: LexiconItem;
  language: Language;
  onClose: () => void;
  onNavigate: (id: string) => void;
  onShare: (item: LexiconItem) => void;
}

const DetailModal: React.FC<DetailModalProps> = ({
  item,
  language,
  onClose,
  onNavigate,
  onShare,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const content = item[language];
  const levelConfig = LEVEL_CONFIG[item.level];
  const pillarConfig = PILLAR_CONFIG[item.pillar];

  const labels = {
    cs: {
      definition: "📖 Definice",
      example: "💡 Příklad",
      tip: "✨ Tip",
      code: "💻 Kód",
      useCases: "🎯 Případy použití",
      mistakes: "⚠️ Časté chyby",
      related: "🔗 Související pojmy",
      minicourses: "🎓 Minikurzy",
      share: "Sdílet",
      copy: "Kopírovat odkaz",
      close: "Zavřít",
      openCourse: "Otevřít kurz →",
      free: "Zdarma",
    },
    en: {
      definition: "📖 Definition",
      example: "💡 Example",
      tip: "✨ Tip",
      code: "💻 Code",
      useCases: "🎯 Use Cases",
      mistakes: "⚠️ Common Mistakes",
      related: "🔗 Related Terms",
      minicourses: "🎓 Minicourses",
      share: "Share",
      copy: "Copy link",
      close: "Close",
      openCourse: "Open course →",
      free: "Free",
    },
  };

  const t = labels[language];

  // Filter minicourses by current language
  const relevantCourses = item.miniCourses?.filter(
    (course) => course.language === language
  );

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Focus trap
  useEffect(() => {
    const modal = modalRef.current;
    if (modal) {
      modal.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Copy code to clipboard
  const copyCode = async () => {
    if (content.codeExample) {
      await navigator.clipboard.writeText(content.codeExample);
      // Could add toast notification here
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        className="modal-content"
        tabIndex={-1}
      >
        {/* Header */}
        <header className="modal-header">
          <div className="modal-title-section">
            <h2 id="modal-title" className="modal-title">
              {content.term}
            </h2>
            <div className="modal-meta">
              <span
                className={`level-badge ${item.level.toLowerCase()}`}
              >
                {levelConfig.icon} {levelConfig.name[language]}
              </span>
              <span
                className={`pillar-badge pillar-${item.pillar.toLowerCase()}`}
                style={{ width: "auto", padding: "4px 8px" }}
              >
                {pillarConfig.icon} {pillarConfig.name[language]}
              </span>
              <span className="card-category">
                📁 {item.category}
              </span>
            </div>
          </div>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t.close}
          >
            ✕
          </button>
        </header>

        {/* Body */}
        <div className="modal-body">
          {/* Definition Section */}
          <section className="modal-section">
            <h3 className="modal-section-title">{t.definition}</h3>
            <div className="modal-section-content">
              <p><strong>{content.shortDefinition}</strong></p>
              <p style={{ marginTop: "12px" }}>{content.definition}</p>
            </div>
          </section>

          {/* Example Section */}
          {content.example && (
            <section className="modal-section">
              <h3 className="modal-section-title">{t.example}</h3>
              <div className="example-box">
                <p className="modal-section-content">{content.example}</p>
              </div>
            </section>
          )}

          {/* Tip Section */}
          {content.tip && (
            <section className="modal-section">
              <h3 className="modal-section-title">{t.tip}</h3>
              <div className="tip-box">
                <span className="tip-box-icon">💡</span>
                <p className="modal-section-content">{content.tip}</p>
              </div>
            </section>
          )}

          {/* Code Example Section */}
          {content.codeExample && (
            <section className="modal-section">
              <h3 className="modal-section-title">{t.code}</h3>
              <div className="code-block">
                <div className="code-block-header">
                  <span className="code-block-lang">TypeScript</span>
                  <button className="code-block-copy" onClick={copyCode}>
                    📋 {t.copy}
                  </button>
                </div>
                <div className="code-block-content">
                  <pre>
                    <code>{content.codeExample}</code>
                  </pre>
                </div>
              </div>
            </section>
          )}

          {/* Use Cases Section */}
          {content.useCases && content.useCases.length > 0 && (
            <section className="modal-section">
              <h3 className="modal-section-title">{t.useCases}</h3>
              <ul style={{ paddingLeft: "20px" }}>
                {content.useCases.map((useCase, index) => (
                  <li
                    key={index}
                    className="modal-section-content"
                    style={{ marginBottom: "8px" }}
                  >
                    {useCase}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Common Mistakes Section */}
          {content.commonMistakes && content.commonMistakes.length > 0 && (
            <section className="modal-section">
              <h3 className="modal-section-title">{t.mistakes}</h3>
              <ul style={{ paddingLeft: "20px" }}>
                {content.commonMistakes.map((mistake, index) => (
                  <li
                    key={index}
                    className="modal-section-content"
                    style={{ marginBottom: "8px", color: "var(--color-warning)" }}
                  >
                    {mistake}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Related Terms Section */}
          {item.relatedIds && item.relatedIds.length > 0 && (
            <section className="modal-section">
              <h3 className="modal-section-title">{t.related}</h3>
              <div className="related-terms">
                {item.relatedIds.map((relatedId) => (
                  <button
                    key={relatedId}
                    className="related-term-btn"
                    onClick={() => onNavigate(relatedId)}
                  >
                    {relatedId}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Minicourses Section */}
          {relevantCourses && relevantCourses.length > 0 && (
            <section className="minicourses-section">
              <h3 className="modal-section-title">
                {t.minicourses} ({relevantCourses.length})
              </h3>
              <div className="minicourses-grid">
                {relevantCourses.map((course) => (
                  <MiniCourseCard
                    key={course.id}
                    course={course}
                    language={language}
                  />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <footer className="modal-footer">
          <div className="modal-footer-actions">
            <button
              className="modal-footer-btn"
              onClick={() => onShare(item)}
            >
              🔗 {t.share}
            </button>
            {content.resources && content.resources.length > 0 && (
              <a
                href={content.resources[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-footer-btn"
              >
                📚 {content.resources[0].title}
              </a>
            )}
          </div>
          <button
            className="modal-footer-btn primary"
            onClick={onClose}
          >
            {t.close}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default DetailModal;
